/* ============================================================================
   PG — Motor do carrinho (gaveta + página do carrinho)
   ----------------------------------------------------------------------------
   Regras que sustentam este arquivo:

   1) TODA escrita no carrinho é endereçada por CHAVE de linha (item.key), nunca
      por posição/índice. Antes usava-se /cart/update.js com array posicional e
      change.js com o número da linha; como o motor de desconto do brinde divide
      e reordena as linhas, a gravação caía na linha do produto errado.

   2) TODA escrita passa por uma FILA SERIAL. Nunca há duas gravações em voo ao
      mesmo tempo, então não há corrida com o re-split do brinde nem com apps
      que interceptam o carrinho (Edrone/EasyGift).

   3) O estado é sempre relido do servidor (/cart.js) imediatamente antes de
      gravar. Nada é decidido em cima de um snapshot velho.

   4) Renderizar NÃO escreve. O merge de linhas divididas é só de exibição
      (feito no Liquid, em snippets/halo-side-cart.liquid). O antigo
      pgMergeDuplicateLineItems gravava no carrinho a cada abertura da gaveta e
      agrupava só por variant_id, fundindo linhas com propriedades diferentes e
      apagando a configuração do cliente. Foi removido.

   XHR (e não fetch) nas gravações: o Edrone embrulha window.fetch e re-divide
   as linhas ao detectar mudança.
   ============================================================================ */
(function () {
  'use strict';

  /* ---------- fila serial de escrita ---------- */
  var fila = Promise.resolve();
  function enfileirar(tarefa) {
    var p = fila.then(tarefa);
    fila = p.catch(function () {});
    return p;
  }

  /* ---------- primitivas ---------- */
  function postJSON(url, body) {
    return new Promise(function (resolve, reject) {
      var xhr = new XMLHttpRequest();
      xhr.open('POST', url, true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
      xhr.onload = function () {
        var dados = null;
        try { dados = JSON.parse(xhr.responseText); } catch (e) {}
        if (xhr.status >= 200 && xhr.status < 300) resolve(dados);
        else reject(dados || new Error('cart ' + xhr.status));
      };
      xhr.onerror = function () { reject(new Error('cart network')); };
      xhr.send(JSON.stringify(body));
    });
  }

  function lerCarrinho() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' })
      .then(function (r) { return r.json(); });
  }

  /* Grava várias linhas de uma vez, cada uma pela sua CHAVE, numa única requisição.
     Isso importa: em requisições separadas o motor de desconto do brinde roda ENTRE
     elas e re-divide a linha, e a segunda gravação cai em cima de um estado que já
     mudou (medido: zerar a linha extra e depois gravar 6 na principal resultava em 8).
     Num único /cart/update.js o motor roda uma vez só, no fim, e o total fica exato.
     mapa = { "<chave da linha>": quantidade }. Retorna o carrinho já atualizado. */
  function atualizarLinhas(mapa) {
    return postJSON('/cart/update.js', { updates: mapa });
  }

  /* ---------- regra de agrupamento ----------
     Precisa bater exatamente com snippets/pg-line-has-props.liquid. */
  function temPropsReais(item) {
    var props = (item && item.properties) || {};
    return Object.keys(props).some(function (chave) {
      var valor = props[chave];
      if (valor === '' || valor === null || valor === undefined) return false;
      if (chave.charAt(0) === '_') return false;
      var pre = chave.slice(0, 4);
      if (pre === 'Peça' || pre === 'Peca') return false;
      return true;
    });
  }

  /* Linhas que a tela exibe como UMA linha só: mesmo variant e sem props reais. */
  function grupoDaLinha(itens, variantId) {
    return itens.filter(function (it) {
      return String(it.variant_id) === String(variantId) && !temPropsReais(it);
    });
  }

  function somaQtd(itens) {
    return itens.reduce(function (s, it) { return s + it.quantity; }, 0);
  }

  /* Chaves que uma linha da tela controla (data-merged-keys; data-line como fallback,
     que é o caso da página do carrinho, onde não há merge visual). */
  function chavesDaLinha(el) {
    if (!el || !el.getAttribute) return [];
    var brutas = el.getAttribute('data-merged-keys') || el.getAttribute('data-line') || '';
    return brutas.split(',').map(function (s) { return s.trim(); }).filter(Boolean);
  }

  /* Acha, no carrinho atual, a linha correspondente a um conjunto de chaves. */
  function acharAlvo(itens, chaves) {
    for (var i = 0; i < itens.length; i++) {
      if (chaves.indexOf(itens[i].key) !== -1) return itens[i];
    }
    return null;
  }

  /* ---------- operações ---------- */

  /* Grava o total do grupo numa requisição só: a primeira linha recebe tudo, as
     demais zeram. O Shopify pode redistribuir entre as linhas depois (parte com
     desconto / parte sem) — o que importa é que a SOMA fica igual à pedida. */
  function gravarGrupo(variantId, desejada, carrinhoConhecido) {
    var inicio = carrinhoConhecido ? Promise.resolve(carrinhoConhecido) : lerCarrinho();
    return inicio.then(function (carrinho) {
      var grupo = grupoDaLinha(carrinho.items || [], variantId);
      if (!grupo.length) return carrinho;
      var mapa = {};
      grupo.forEach(function (it, n) { mapa[it.key] = (n === 0 ? desejada : 0); });
      return atualizarLinhas(mapa);
    });
  }

  /* agrupavel = a tela desenhou as linhas divididas como UMA linha só (gaveta,
     onde existe data-merged-keys). Na página do carrinho cada linha aparece
     separada, então ali cada uma responde só por si — tratar por grupo apagaria
     a linha irmã que o cliente está vendo logo abaixo. */
  function definirQuantidade(chaves, desejada, agrupavel) {
    return lerCarrinho().then(function (carrinho) {
      var itens = carrinho.items || [];
      var alvo = acharAlvo(itens, chaves);
      if (!alvo) return carrinho;

      /* Linha com propriedade real nunca é agrupada: mexe só nela. */
      if (!agrupavel || temPropsReais(alvo)) {
        var soLinha = {};
        soLinha[alvo.key] = desejada;
        return atualizarLinhas(soLinha);
      }

      var variantId = String(alvo.variant_id);
      return gravarGrupo(variantId, desejada, carrinho).then(function (novo) {
        /* Uma passada corretiva: o motor de desconto pode ter re-dividido a
           linha durante a gravação. Confere o total e corrige uma única vez. */
        var total = somaQtd(grupoDaLinha(novo.items || [], variantId));
        if (total === desejada) return novo;
        return gravarGrupo(variantId, desejada, novo);
      });
    });
  }

  function removerLinha(chaves, agrupavel) {
    return lerCarrinho().then(function (carrinho) {
      var itens = carrinho.items || [];
      var alvo = acharAlvo(itens, chaves);
      if (!alvo) return carrinho;

      /* Agrupada: remove todas as partes daquele variant. Com props: só a linha. */
      var aRemover = (!agrupavel || temPropsReais(alvo))
        ? [alvo.key]
        : grupoDaLinha(itens, alvo.variant_id).map(function (it) { return it.key; });

      var mapa = {};
      aRemover.forEach(function (chave) { mapa[chave] = 0; });
      return atualizarLinhas(mapa);
    });
  }

  /* ---------- render ---------- */
  function renderizar(carrinho) {
    if (!carrinho) return carrinho;
    var halo = window.halo || {};
    if (document.body.classList.contains('template-cart')) {
      if (halo.updateCart) halo.updateCart(carrinho);
    } else if (halo.updateSidebarCart) {
      halo.updateSidebarCart(carrinho);
    }
    if (typeof carrinho.item_count === 'number') {
      document.querySelectorAll('[data-cart-count]').forEach(function (el) {
        el.textContent = carrinho.item_count;
      });
    }
    document.dispatchEvent(new CustomEvent('cart-update', { detail: carrinho }));
    return carrinho;
  }

  /* ---------- eventos ----------
     Ambos em CAPTURA + stopImmediatePropagation: assumem o lugar dos handlers
     posicionais do tema (updateQuantityItemQuickCart / removeItemQuickCart). */

  document.addEventListener('change', function (e) {
    var input = e.target;
    if (!input || !input.hasAttribute || !input.hasAttribute('data-cart-quantity')) return;
    if (input.closest && input.closest('.quick-order-list__contents')) return;

    var chaves = chavesDaLinha(input);
    if (!chaves.length) return; /* sem chave: deixa o fluxo do tema seguir */
    var agrupavel = input.hasAttribute('data-merged-keys');

    e.stopImmediatePropagation();
    e.preventDefault();

    var desejada = parseInt(input.value, 10);
    if (!desejada || desejada < 1) { desejada = 1; }
    var max = parseInt(input.getAttribute('max') || '0', 10);
    if (max > 0 && desejada > max) { desejada = max; }
    input.value = desejada;

    enfileirar(function () {
      return definirQuantidade(chaves, desejada, agrupavel).then(renderizar);
    }).catch(function () {});
  }, true);

  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest ? e.target.closest('[data-cart-remove]') : null;
    if (!btn) return;

    var chaves = chavesDaLinha(btn);
    if (!chaves.length) return;
    var agrupavel = btn.hasAttribute('data-merged-keys');

    e.preventDefault();
    e.stopImmediatePropagation();

    enfileirar(function () {
      return removerLinha(chaves, agrupavel).then(renderizar);
    }).catch(function () {});
  }, true);

  /* ---------- rede de segurança ----------
     Shopify.changeItem/removeItem do tema gravam pelo ÍNDICE da linha
     (getCartUpdate({line: index})), e o data-index do tema renderiza sempre "1"
     porque item.index não existe no objeto line_item — ou seja, apagavam a
     primeira linha do carrinho. Aqui passam a usar a chave quando ela existe.
     Roda no DOMContentLoaded, depois dos scripts defer (cart-components.js). */
  document.addEventListener('DOMContentLoaded', function () {
    if (!window.Shopify) return;
    var origChange = window.Shopify.changeItem;
    var origRemove = window.Shopify.removeItem;

    window.Shopify.changeItem = function (chave, qtd, index, cb) {
      if (!chave) {
        if (typeof origChange === 'function') origChange(chave, qtd, index, cb);
        return;
      }
      enfileirar(function () {
        return definirQuantidade([chave], qtd, false).then(function (c) {
          if (typeof cb === 'function') cb(c);
          return c;
        });
      }).catch(function () {});
    };

    window.Shopify.removeItem = function (chave, index, cb) {
      if (!chave) {
        if (typeof origRemove === 'function') origRemove(chave, index, cb);
        return;
      }
      enfileirar(function () {
        return removerLinha([chave], false).then(function (c) {
          if (typeof cb === 'function') cb(c);
          return c;
        });
      }).catch(function () {});
    };
  });

  /* ---------- KIT brinde ----------
     add.js por XHR pelo mesmo motivo (não ser interceptado), agora na fila. */
  var PG_KIT_HANDLE = window.PG_KIT_HANDLE || 'kit-instalacao';

  window.pgAddKitBrinde = function (btn) {
    /* Brinde desligado no painel (Brinde & Upsell): nao adiciona nada. */
    if (window.PG_KIT_ATIVO === false) return;
    if (window.__pgKitAdding) return;
    window.__pgKitAdding = true;
    var variantId = btn ? btn.getAttribute('data-kit-variant') : null;
    if (btn) btn.disabled = true;

    enfileirar(function () {
      return lerCarrinho().then(function (carrinho) {
        var jaTem = (carrinho.items || []).some(function (i) { return i.handle === PG_KIT_HANDLE; });
        if (jaTem || !variantId) return carrinho;
        return postJSON('/cart/add.js?_easygift_internal=true', {
          id: parseInt(variantId, 10),
          quantity: 1
        }).then(lerCarrinho);
      }).then(renderizar);
    })
      .catch(function () {})
      .then(function () {
        window.__pgKitAdding = false;
        if (btn) btn.disabled = false;
      });
  };

  /* Exposto para depuração no console. */
  window.pgCart = {
    definirQuantidade: definirQuantidade,
    removerLinha: removerLinha,
    lerCarrinho: lerCarrinho,
    temPropsReais: temPropsReais
  };
})();
